import { useMemo } from 'react';

import { isFinanceProject, isGlobalProject } from 'config';
import { formatToDate, lodash } from 'helpers';
import { useTranslation } from 'libs/i18n';
import {
  CompanyNameSection,
  WithdrawalAccountsSection,
  CompanyRegistrationAddressSection,
  CompanyResidentialAddressSection,
  CompanyStateRegistrationSection,
  AccountCodesSection,
  ActivitySection,
  AdditionalDocumentsSection,
  ServiceContractSection,
  RiskLevelSection,
  ResultCheckSection,
  USTaxResidencySection,
  W8BENFormSection,
  RepresentativeSection,
  DebtsSection,
} from 'modules/client/common/components/sections';
import { CountryRiskField } from 'modules/client/common/feature';

import {
  CompanyTaxResidencySection,
  CompanyActivitiesSection,
  CompanyBeneficiariesSection,
  CompanyBranchesSection,
  CompanyContactsSection,
  CompanyHeadsSection,
  CompanyLicenseSection,
  CompanyOrganizationalAndLegalFormSection,
  CompanyRegistrationPlaceSection,
  CompanyStructureSection,
  CompanyFinanceFinancialMonitoringSection,
  CompanyOrderSubmissionContactsSection,
  GeneralSection,
  TargetedFinancialInstrumentsSection,
  SourcesOfIncomeSection,
  IdentificationNumbersSection,
  CompanyActivitySection,
  InvestmentPurposeAndNatureSection,
  CompanyConstituentDocumentsSection,
  CompanyKnowledgeAndExperienceSection,
  CompanyGlobalFinancialMonitoringSection,
  LegalClientDocumentsSection,
  CompanyConfirmStructureSection,
  OpenAccountOrderSection,
  CompanyBankAccountsSection,
  CompanyFinancingSourcesSection,
  CompanyBusinessRelationshipSection,
  CompanyDateOfStructureChangeSection,
} from '../sections';

import {
  getLegalClientPersonalAccountFormSectionKeys,
  getLegalClientMainFormSectionKeys,
  getLegalClientClassificationFormSectionKeys,
  getLegalClientRiskLevelFormSectionKeys,
  getLegalClientFinancialMonitoringFormSectionKeys,
  getLegalClientOtherFormSectionKeys,
  getLegalClientCompanyHeadsFormSectionKeys,
  getRepresentativeInfoContent,
} from './helpers';
import { LegalClientSectionsProps } from './types';

export const useLegalClientSections = ({
  countries,
  countriesLoading,
  heads,
  legalHeads,
  beneficiaries,
  unidentifiableBeneficiaryLetterFiles,
  branches,
  managers,
  managersLoading,
  visibleOnboardingStatuses,
  hideActivitySection,
  bodies,
  isEditablePersonalAccountNumber,
  mainInfo,
  representativeInfo,
  onClickHead,
  onClickLegalHead,
  onClickAddHead,
  onClickAddBeneficiary,
  onClickAddLegalHead,
  onClickBeneficiary,
  onDeleteBeneficiary,
  onDeleteHead,
  onDeleteLegalHead,
  onAddBody,
  onUpdateBody,
  onArchiveBody,
  onDearchiveBody,
  onDeleteBody,
  onArchiveHead,
  onArchiveLegalHead,
  onDearchiveHead,
  onDearchiveLegalHead,
  onArchiveBeneficiary,
  onDearchiveBeneficiary,
  onClickRepresentative,
  onClickAddBranch,
  onClickBranch,
  onArchiveBranch,
  onDearchiveBranch,
  onDeleteBranch,
}: LegalClientSectionsProps) => {
  const { t } = useTranslation();

  const isGlobal = isGlobalProject();
  const isFinance = isFinanceProject();

  const representativeInfoContent = useMemo(
    () => getRepresentativeInfoContent(t, representativeInfo),
    [representativeInfo, t]
  );

  const tabs = useMemo(
    () =>
      lodash.compact([
        {
          label: t('client.legal.sections.personalAccount'),
          id: 'personalAccount',
          content: (
            <>
              <AccountCodesSection
                isEditablePersonalAccountNumber={
                  isEditablePersonalAccountNumber
                }
                managers={managers}
                managersLoading={managersLoading}
              />
              {isFinance && <OpenAccountOrderSection />}
              <ServiceContractSection enabledDocuments={isFinance} />
            </>
          ),
          formSectionKeys: getLegalClientPersonalAccountFormSectionKeys(),
        },
        {
          label: t('client.legal.sections.main'),
          id: 'main',
          content: (
            <>
              <CompanyNameSection showEn />
              <CompanyOrganizationalAndLegalFormSection
                enabledOrganizationType={isFinance}
              />
              <CompanyStateRegistrationSection
                enabledLastStateReRegistrationDate
              />
              <CompanyRegistrationPlaceSection
                countries={countries}
                countriesLoading={countriesLoading}
              />
              {isGlobal && <USTaxResidencySection />}
              <CompanyTaxResidencySection
                countries={countries}
                countriesLoading={countriesLoading}
              />
              <CompanyLicenseSection />
              {isGlobal && <IdentificationNumbersSection />}
            </>
          ),
          formSectionKeys: getLegalClientMainFormSectionKeys(),
        },
        {
          label: t('client.legal.sections.withdrawalAccounts'),
          id: 'withdrawalAccounts',
          content: <WithdrawalAccountsSection />,
          formSectionKeys: ['withdrawalAccounts'],
        },
        representativeInfoContent && {
          label: t('client.legal.sections.representative'),
          id: 'representative',
          content: (
            <RepresentativeSection
              representativeData={representativeInfoContent}
              onClickRepresentative={onClickRepresentative}
            />
          ),
          formSectionKeys: [],
        },
        {
          label: t('client.legal.sections.contacts'),
          id: 'contacts',
          content: (
            <>
              <CompanyRegistrationAddressSection
                countries={countries}
                countriesLoading={countriesLoading}
                enabledFiles={isGlobal}
              />
              <CompanyResidentialAddressSection
                countries={countries}
                countriesLoading={countriesLoading}
              />
              <CompanyContactsSection />
              <CompanyOrderSubmissionContactsSection />
            </>
          ),
          formSectionKeys: [
            'companyRegistrationAddress',
            'companyResidentialAddress',
            'companyContacts',
            'orderSubmissionContacts',
          ],
        },
        {
          label: t('client.legal.sections.companyHeads'),
          id: 'companyHeads',
          content: (
            <>
              {isFinance && <CompanyDateOfStructureChangeSection />}
              <CompanyHeadsSection
                heads={heads}
                legalHeads={legalHeads}
                onArchiveHead={onArchiveHead}
                onArchiveLegalHead={onArchiveLegalHead}
                onClickAddHead={onClickAddHead}
                onClickAddLegalHead={onClickAddLegalHead}
                onClickHead={onClickHead}
                onClickLegalHead={onClickLegalHead}
                onDearchiveHead={onDearchiveHead}
                onDearchiveLegalHead={onDearchiveLegalHead}
                onDeleteHead={onDeleteHead}
                onDeleteLegalHead={onDeleteLegalHead}
              />
            </>
          ),
          formSectionKeys: getLegalClientCompanyHeadsFormSectionKeys(),
        },
        {
          label: t('client.legal.sections.classification'),
          id: 'classification',
          content: (
            <>
              <GeneralSection
                showClassification={isGlobal}
                showClientType={isGlobal}
              />
              <CompanyActivitiesSection enabledOKPOCode={isFinance} />
              {isGlobal && <CompanyActivitySection />}
            </>
          ),
          formSectionKeys: getLegalClientClassificationFormSectionKeys(),
        },
        {
          label: t('client.legal.sections.beneficiaries'),
          id: 'beneficiaries',
          content: (
            <CompanyBeneficiariesSection
              beneficiaries={beneficiaries}
              unidentifiableBeneficiaryLetterFiles={
                unidentifiableBeneficiaryLetterFiles
              }
              onArchiveBeneficiary={onArchiveBeneficiary}
              onClickAddBeneficiary={onClickAddBeneficiary}
              onClickBeneficiary={onClickBeneficiary}
              onDearchiveBeneficiary={onDearchiveBeneficiary}
              onDeleteBeneficiary={onDeleteBeneficiary}
            />
          ),
          formSectionKeys: [],
        },
        {
          label: t('client.legal.sections.documents'),
          id: 'documents',
          content: (
            <>
              <CompanyConstituentDocumentsSection />
              <CompanyConfirmStructureSection />
              <LegalClientDocumentsSection />
              <AdditionalDocumentsSection />
            </>
          ),
          formSectionKeys: [
            'constituentDocsFiles',
            'confirmStructure',
            'documentsFiles',
            'additionalDocumentsFiles',
          ],
        },
        {
          label: t('client.legal.sections.companyStructure'),
          id: 'companyStructure',
          content: (
            <CompanyStructureSection
              bodies={bodies}
              onAddBody={onAddBody}
              onArchiveBody={onArchiveBody}
              onDearchiveBody={onDearchiveBody}
              onDeleteBody={onDeleteBody}
              onUpdateBody={onUpdateBody}
            />
          ),
          formSectionKeys: [],
        },
        isFinance && {
          label: t('client.legal.companyBranches.title'),
          id: 'branches',
          content: (
            <CompanyBranchesSection
              branches={branches}
              onArchiveBranch={onArchiveBranch}
              onClickAddBranch={onClickAddBranch}
              onClickBranch={onClickBranch}
              onDearchiveBranch={onDearchiveBranch}
              onDeleteBranch={onDeleteBranch}
            />
          ),
          formSectionKeys: [],
        },
        {
          label: t('client.legal.sections.riskLevel'),
          id: 'riskLevel',
          content: (
            <>
              <RiskLevelSection />
              {isGlobal && <SourcesOfIncomeSection />}
              {isFinance && (
                <CompanyBankAccountsSection
                  countries={countries}
                  countriesLoading={countriesLoading}
                />
              )}
              {isFinance && <CompanyFinancingSourcesSection />}
            </>
          ),
          formSectionKeys: getLegalClientRiskLevelFormSectionKeys(),
        },
        {
          label: t('client.legal.sections.financialMonitoring'),
          id: 'financialMonitoring',
          content: (
            <>
              {isFinance && (
                <CompanyFinanceFinancialMonitoringSection
                  hasBranches={!!branches.length}
                />
              )}
              {isGlobal && <CompanyGlobalFinancialMonitoringSection />}
            </>
          ),
          formSectionKeys: getLegalClientFinancialMonitoringFormSectionKeys(),
        },
        {
          label: t('client.legal.sections.resultCheck'),
          id: 'resultCheck',
          content: <ResultCheckSection statuses={visibleOnboardingStatuses} />,
          formSectionKeys: [],
        },
        {
          label: t('client.legal.sections.other'),
          id: 'other',
          content: (
            <>
              {!hideActivitySection && (
                <ActivitySection brightCodeEnabled isBaseForm />
              )}
              {isFinance && <CompanyBusinessRelationshipSection />}
              {isFinance && <DebtsSection isEntrepreneur />}
              {isGlobal && <W8BENFormSection formType="W8BENEForm" />}
              {isGlobal && <CompanyKnowledgeAndExperienceSection />}
              {isGlobal && <TargetedFinancialInstrumentsSection />}
              {isGlobal && <InvestmentPurposeAndNatureSection />}
            </>
          ),
          formSectionKeys: getLegalClientOtherFormSectionKeys(),
        },
      ]),
    [
      beneficiaries,
      bodies,
      branches,
      countries,
      countriesLoading,
      heads,
      hideActivitySection,
      isEditablePersonalAccountNumber,
      isFinance,
      isGlobal,
      legalHeads,
      managers,
      managersLoading,
      onAddBody,
      onArchiveBeneficiary,
      onArchiveBody,
      onArchiveBranch,
      onArchiveHead,
      onArchiveLegalHead,
      onClickAddBeneficiary,
      onClickAddBranch,
      onClickAddHead,
      onClickAddLegalHead,
      onClickBeneficiary,
      onClickBranch,
      onClickHead,
      onClickLegalHead,
      onClickRepresentative,
      onDearchiveBeneficiary,
      onDearchiveBody,
      onDearchiveBranch,
      onDearchiveHead,
      onDearchiveLegalHead,
      onDeleteBeneficiary,
      onDeleteBody,
      onDeleteBranch,
      onDeleteHead,
      onDeleteLegalHead,
      onUpdateBody,
      representativeInfoContent,
      t,
      unidentifiableBeneficiaryLetterFiles,
      visibleOnboardingStatuses,
    ]
  );

  const info = useMemo(
    () =>
      mainInfo
        ? [
            [
              {
                label: t('client.legal.mainInfo.representativeFullName'),
                value: mainInfo.representativeFullName,
              },
              {
                label: t('client.legal.mainInfo.representativeEmail'),
                value: mainInfo.representativeEmail,
              },
              {
                label: t('client.legal.mainInfo.representativePhone'),
                value: mainInfo.representativePhone,
              },
            ],
            [
              {
                label: t('client.legal.mainInfo.country'),
                value: mainInfo.country?.name,
                countryRisk: {
                  country: mainInfo.country,
                  field: CountryRiskField.CompanyLegalAddress,
                },
              },
              {
                label: t('client.legal.mainInfo.tariff'),
                value: mainInfo.tariff,
              },
              {
                label: t('client.legal.mainInfo.contractNumber'),
                value: mainInfo.contractNumber,
              },
            ],
            [
              {
                label: t('client.legal.mainInfo.contractDate'),
                value:
                  mainInfo.contractDate && formatToDate(mainInfo.contractDate),
              },
              {
                label: t('client.legal.mainInfo.accountNumber'),
                value: mainInfo.accountNumber,
              },
              {
                label: t('client.legal.mainInfo.accountOpeningDate'),
                value:
                  mainInfo.accountOpeningDate &&
                  formatToDate(mainInfo.accountOpeningDate),
              },
            ],
            [
              {
                label: t(
                  'client.legal.mainInfo.centralDepositoryAccountNumber'
                ),
                value: mainInfo.centralDepositoryAccountNumber,
              },
              {
                label: t(
                  'client.legal.mainInfo.centralDepositoryAccountOpeningDate'
                ),
                value:
                  mainInfo.centralDepositoryAccountOpeningDate &&
                  formatToDate(mainInfo.centralDepositoryAccountOpeningDate),
              },
              {
                label: t('client.legal.mainInfo.code'),
                value: mainInfo.code,
              },
            ],
          ]
        : undefined,
    [mainInfo, t]
  );

  return { tabs, info };
};
